/**
 * Operations overview (owner product review §24). Every number is a PostgreSQL read model; money
 * metrics have no source before the ledger phases and render «—» with the reason — never a fake zero.
 */
import {
  Amount,
  AppContainer,
  Badge,
  Button,
  Card,
  formatRelative,
  KpiTile,
  PageHeader,
  StatusBadge,
} from '@hopex/ui';
import { Link } from '@tanstack/react-router';
import { ChevronDown, RefreshCw } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  dt,
  type FinanceOverview,
  type Overview,
  type ProviderHealth,
  type ProviderLane,
} from '../api.ts';
import { RUN_STATUS } from '../lib/money-labels.ts';
import { auditLabel, eventLabel, SEVERITY } from '../lib/labels.ts';
import { Remote, useRemote } from '../lib/remote.tsx';
import { SystemHealthCard } from './SystemHealth.tsx';

const n = (v: number) => <bdi className="hx-num">{v.toLocaleString('en-US')}</bdi>;
const NOT_ACTIVE = 'ماژول مالی هنوز فعال نشده است';
const toman = (v: string | null) => (v === null ? null : <Amount value={v} unit="TOMAN" />);

/** Kill-switch / reconciliation state and money queues (Phase 4, PostgreSQL truth). */
function MoneyStatus() {
  const r = useRemote<FinanceOverview>('/finance/overview');
  if (r.error)
    return (
      <p role="alert" className="m-0 text-[12.5px] text-negative">
        وضعیت مالی بارگذاری نشد: {r.error}
      </p>
    );
  const f = r.data;
  if (!f) return null;
  const last = f.lastReconciliation;
  return (
    <div className="flex flex-col gap-3" data-money-status={f.halted ? 'HALTED' : 'OPEN'}>
      <div
        className={`flex flex-wrap items-center justify-between gap-2 rounded-md border px-4 py-3 text-[13px] ${f.halted ? 'border-negative/50 bg-negative-tint text-negative' : 'border-line bg-surface-1'}`}
      >
        <span className="font-semibold">
          {f.halted ? 'عملیات مالی حساس متوقف است' : 'عملیات مالی حساس فعال است'}
        </span>
        <span className="text-[12px] text-fg-muted">
          آخرین تطبیق:{' '}
          {last
            ? `${RUN_STATUS[last.status]?.label ?? last.status} · ${dt(last.startedAt)}`
            : 'هنوز اجرا نشده'}
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <KpiTile label="واریز در انتظار وجه" value={n(f.pendingDeposits - f.depositsInReview)} />
        <KpiTile
          label="واریز نیازمند بررسی"
          value={n(f.depositsInReview)}
          tone={f.depositsInReview ? 'caution' : 'neutral'}
        />
        <KpiTile label="برداشت در جریان" value={n(f.pendingWithdrawals)} />
        <KpiTile
          label="پرونده‌ی تطبیق باز"
          value={n(f.openCases)}
          tone={f.openCases ? 'negative' : 'neutral'}
        />
        <KpiTile label="شاهد بانکی بدون تطبیق" value={n(f.openEvidence)} />
        <KpiTile label="بدهی به کاربران" value={toman(f.userLiabilitiesToman)} />
      </div>
    </div>
  );
}

function Group({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section aria-label={title} className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between">
        <h2 className="m-0 text-[13px] font-bold text-fg-muted">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

const LANES: Record<ProviderLane, string> = {
  sms: 'پیامک',
  phoneOwnership: 'تطبیق مالکیت موبایل',
  civilIdentity: 'استعلام هویت',
  bankIdentity: 'مالکیت کارت و شبا',
};

function laneState(states: Record<string, string>): 'ok' | 'down' | 'testing' {
  const v = Object.values(states);
  if (v.includes('OPEN')) return 'down';
  if (v.includes('HALF_OPEN')) return 'testing';
  return 'ok';
}
const BREAKER_SHORT = { ok: 'بدون خطای اخیر', down: 'قطع موقت', testing: 'در حال آزمایش' } as const;

/* ───────── Phase 8 control room (PostgreSQL read model; every tile deep-links its queue) ───────── */

export interface OpsSummary {
  asOf: string;
  crypto: {
    depositsSubmitted: number;
    depositsInReview: number;
    depositsOldest: string | null;
    withdrawalsProcessing: number;
    withdrawalsOnHold: number;
    withdrawalsOldest: string | null;
  };
  screeningOpen: number;
  conversions: { today: number; activeQuotes: number };
  support: { waitingAgent: number; waitingUser: number; oldestWaiting: string | null };
  alerts: {
    open: number;
    latest: {
      id: string;
      kind: string;
      severity: string;
      subjectType: string;
      subjectId: string | null;
      at: string;
    }[];
  };
  notificationsFailed24h: number;
  rates: { pair: string; healthy: boolean; method: string | null; at: string | null }[];
}
type OpsRemote = { data: OpsSummary | null; error: string | null; reload: () => void };

/** A KPI that is a link to its queue; «—» with the reason when the summary could not load. */
function OpsKpi({
  to,
  label,
  ops,
  pick,
  caution,
  hint,
}: {
  to: string;
  label: string;
  ops: OpsRemote;
  pick: (s: OpsSummary) => number;
  caution?: boolean;
  hint?: (s: OpsSummary) => string | undefined;
}) {
  const v = ops.data ? pick(ops.data) : null;
  const h = ops.data && hint ? hint(ops.data) : undefined;
  return (
    <Link
      to={to}
      className="rounded-lg outline-hidden focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus"
      data-ops-kpi={to}
    >
      <KpiTile
        label={label}
        value={v === null ? null : n(v)}
        tone={v && caution ? 'caution' : 'neutral'}
        {...(h ? { hint: h } : {})}
        {...(ops.error ? { unavailableReason: 'خلاصه‌ی عملیات بارگذاری نشد' } : {})}
      />
    </Link>
  );
}

const ALERT_KIND: Record<string, string> = {
  CRYPTO_DEPOSIT_AGED_REVIEW: 'واریز رمزارز معطل در بررسی',
  CRYPTO_DUPLICATE_TXID: 'TXID تکراری',
  CRYPTO_SCREENING_BLOCKED: 'غربالگری: مسدود',
  CRYPTO_SCREENING_UNAVAILABLE: 'غربالگری در دسترس نیست',
  CRYPTO_WITHDRAWAL_SECURITY_HOLD: 'برداشت در توقف امنیتی',
  INTEGRITY_KILL_SWITCH_ON: 'توقف اضطراری عملیات مالی فعال است',
  INTEGRITY_RECONCILIATION_FAILED: 'تطبیق ناموفق یا دارای مغایرت',
  INTEGRITY_RECONCILIATION_STALE: 'تطبیق بیش از ۱۵ دقیقه اجرا نشده',
  INTEGRITY_RECONCILIATION_STALE_60M: 'تطبیق بیش از یک ساعت اجرا نشده',
  INTEGRITY_JOB_BACKLOG: 'صف کارهای پس‌زمینه عقب افتاده',
  RATE_USDT_IRT_UNAVAILABLE: 'نرخ تتر به تومان بیش از ۵ دقیقه ناموجود است',
  RATE_USDT_IRT_JUMP: 'نرخ تتر به تومان در یک ساعت بیش از ۳٪ تغییر کرده است',
};
const SEVERITY_FA: Record<string, string> = { CRITICAL: 'بحرانی', WARNING: 'هشدار', INFO: 'اطلاع' };
const PAIR_FA = (p: string) => (p === 'USDT/IRT' ? 'تتر / تومان' : p);

interface RateRow {
  pair: string;
  healthy: boolean;
  method: string | null;
  at: string | null;
}

/** Problems first (no rate, then emergency), then USDT/IRT, then the rest alphabetically. */
const rank = (x: RateRow) =>
  !x.healthy ? 0 : x.method === 'EMERGENCY' ? 1 : x.pair === 'USDT/IRT' ? 2 : 3;
const COMPACT = 6;

function RateLine({ x }: { x: RateRow }) {
  return (
    <li className="flex items-center justify-between gap-2 py-2" data-rate-pair={x.pair}>
      <bdi dir="ltr">{PAIR_FA(x.pair)}</bdi>
      <span className="flex items-center gap-2">
        {x.at && <span className="text-[11.5px] text-fg-subtle">{formatRelative(x.at)}</span>}
        <StatusBadge
          status={x.healthy ? 'completed' : 'rejected'}
          label={
            x.healthy
              ? x.method === 'EMERGENCY'
                ? 'نرخ اضطراری'
                : x.method === 'NIGHT'
                  ? 'نرخ شبانه (ثابت)'
                  : 'سالم (میانه)'
              : x.at
                ? 'ناموجود'
                : 'بدون نرخ'
          }
        />
      </span>
    </li>
  );
}

/**
 * Rate health for many pairs: counts, the pairs that need attention, and the full list on demand
 * (a disclosure with its own scroll area and a filter) instead of one very long card.
 */
function RateHealth({ rates }: { rates: RateRow[] }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const sorted = [...rates].sort((a, b) => rank(a) - rank(b) || a.pair.localeCompare(b.pair));
  const healthy = rates.filter((x) => x.healthy && x.method !== 'EMERGENCY').length;
  const emergency = rates.filter((x) => x.healthy && x.method === 'EMERGENCY').length;
  const down = rates.length - healthy - emergency;
  const filter = q.trim().toUpperCase();
  const all = filter
    ? sorted.filter((x) => x.pair.includes(filter) || PAIR_FA(x.pair).includes(q.trim()))
    : sorted;
  const fa = (n: number) => n.toLocaleString('fa-IR');
  return (
    <div data-rate-health-body>
      <div className="mb-1 flex flex-wrap items-center gap-2 text-[12.5px]" data-rate-summary>
        <Badge tone="positive">سالم {fa(healthy)}</Badge>
        {emergency > 0 && <Badge tone="accent">اضطراری {fa(emergency)}</Badge>}
        <Badge tone={down ? 'negative' : 'neutral'}>ناموجود {fa(down)}</Badge>
      </div>
      <ul className="m-0 list-none divide-y divide-line p-0 text-[13px]">
        {sorted.slice(0, COMPACT).map((x) => (
          <RateLine key={x.pair} x={x} />
        ))}
      </ul>
      {rates.length > COMPACT && (
        <>
          <button
            type="button"
            className="mt-2 flex w-full items-center justify-center gap-1 rounded-md border border-line px-3 py-2 text-[12.5px] font-semibold text-fg-muted hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-accent"
            aria-expanded={open}
            aria-controls="rate-health-all"
            onClick={() => setOpen(!open)}
            data-rate-show-all
          >
            {open ? 'بستن فهرست' : `مشاهده همه (${fa(rates.length)})`}
            <ChevronDown
              aria-hidden
              size={15}
              className={open ? 'rotate-180 transition-transform' : 'transition-transform'}
            />
          </button>
          {open && (
            <div id="rate-health-all" className="mt-2 rounded-md border border-line p-2">
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="جستجوی نماد، مثلاً BTC"
                aria-label="جستجو در نرخ‌ها"
                className="mb-2 w-full rounded-md border border-line bg-surface-2 px-3 py-1.5 text-[12.5px] text-fg"
              />
              <ul
                className="m-0 max-h-[320px] list-none divide-y divide-line overflow-y-auto p-0 pe-1 text-[13px]"
                data-rate-all
              >
                {all.map((x) => (
                  <RateLine key={x.pair} x={x} />
                ))}
                {all.length === 0 && (
                  <li className="py-3 text-center text-fg-muted">نتیجه‌ای پیدا نشد</li>
                )}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ControlRoom({ ops }: { ops: OpsRemote }) {
  const age = (iso: string | null | undefined) =>
    iso ? `قدیمی‌ترین: ${formatRelative(iso)}` : undefined;
  return (
    <Group
      title="مرکز کنترل عملیات"
      action={
        ops.data ? (
          <span className="text-[11.5px] text-fg-subtle">
            به‌روز {formatRelative(ops.data.asOf)}
          </span>
        ) : undefined
      }
    >
      {ops.error && (
        <p role="alert" className="m-0 text-[12.5px] text-negative" data-ops-error>
          خلاصه‌ی عملیات بارگذاری نشد: {ops.error} — مقدار صفر نمایش داده نمی‌شود.
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-control-room>
        <OpsKpi
          to="/crypto-deposits"
          label="واریز رمزارز در بررسی"
          ops={ops}
          caution
          pick={(s) => s.crypto.depositsSubmitted + s.crypto.depositsInReview}
          hint={(s) => age(s.crypto.depositsOldest)}
        />
        <OpsKpi
          to="/crypto-withdrawals"
          label="برداشت رمزارز در جریان"
          ops={ops}
          pick={(s) => s.crypto.withdrawalsProcessing}
          hint={(s) => age(s.crypto.withdrawalsOldest)}
        />
        <OpsKpi
          to="/crypto-withdrawals"
          label="برداشت در توقف امنیتی"
          ops={ops}
          caution
          pick={(s) => s.crypto.withdrawalsOnHold}
        />
        <OpsKpi
          to="/screening"
          label="غربالگری بدون تصمیم"
          ops={ops}
          caution
          pick={(s) => s.screeningOpen}
        />
        <OpsKpi
          to="/support"
          label="پرونده منتظر کارشناس"
          ops={ops}
          caution
          pick={(s) => s.support.waitingAgent}
          hint={(s) => age(s.support.oldestWaiting)}
        />
        <OpsKpi
          to="/support"
          label="پرونده منتظر کاربر"
          ops={ops}
          pick={(s) => s.support.waitingUser}
        />
        <OpsKpi
          to="/conversions"
          label="پیش‌فاکتور فعال (۱۰ ثانیه)"
          ops={ops}
          pick={(s) => s.conversions.activeQuotes}
        />
        <OpsKpi
          to="/notifications"
          label="تحویل ناموفق اعلان (۲۴ ساعت)"
          ops={ops}
          caution
          pick={(s) => s.notificationsFailed24h}
        />
      </div>
      {ops.data && (
        <div className="grid gap-4 xl:grid-cols-2">
          <Card className="p-4 sm:p-5" data-rate-health>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="m-0 text-[14px] font-bold">سلامت نرخ‌ها</h3>
              <Link
                to="/rates"
                className="text-[12.5px] font-semibold text-fg-muted hover:underline"
              >
                نرخ‌ها و منابع
              </Link>
            </div>
            <RateHealth rates={ops.data.rates} />
          </Card>
          <Card className="p-4 sm:p-5" data-ops-alerts>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="m-0 text-[14px] font-bold">هشدارهای عملیاتی باز</h3>
              <Badge tone={ops.data.alerts.open ? 'negative' : 'neutral'}>
                {ops.data.alerts.open.toLocaleString('fa-IR')}
              </Badge>
            </div>
            {ops.data.alerts.latest.length === 0 ? (
              <p className="m-0 text-[13px] text-fg-muted">هشدار بازی وجود ندارد.</p>
            ) : (
              <ul className="m-0 list-none divide-y divide-line p-0 text-[13px]">
                {ops.data.alerts.latest.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-2 py-2">
                    <Link to="/crypto-deposits" className="hover:underline">
                      {ALERT_KIND[a.kind] ?? a.kind}
                    </Link>
                    <span className="flex items-center gap-2">
                      <Badge tone={a.severity === 'CRITICAL' ? 'negative' : 'accent'}>
                        {SEVERITY_FA[a.severity] ?? a.severity}
                      </Badge>
                      <span className="text-[11.5px] text-fg-subtle">{formatRelative(a.at)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </Group>
  );
}

export function OverviewPage() {
  const r = useRemote<Overview>('/overview');
  const p = useRemote<ProviderHealth>('/providers/health');
  const ops = useRemote<OpsSummary>('/ops-summary');
  return (
    <AppContainer>
      <PageHeader
        title="نمای کلی عملیات"
        actions={
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshCw aria-hidden className="size-4" />}
            onPress={() => (r.reload(), p.reload(), ops.reload())}
          >
            به‌روزرسانی
          </Button>
        }
      />
      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <SystemHealthCard />
        <Card className="p-4 sm:p-5">
          <h2 className="m-0 mb-3 text-[14px] font-bold">سلامت سرویس‌ها</h2>
          <Remote r={p}>
            {(h) => (
              <ul
                className="m-0 list-none divide-y divide-line p-0 text-[13px]"
                data-provider-health
              >
                {(Object.keys(LANES) as ProviderLane[]).map((lane) => {
                  const s = laneState(h[lane]);
                  const sim = h.simulated?.[lane] ?? true;
                  return (
                    <li
                      key={lane}
                      className="flex items-center justify-between gap-2 py-2"
                      data-lane-mode={sim ? 'simulator' : 'live'}
                    >
                      <span>{LANES[lane]}</span>
                      <span className="flex items-center gap-2">
                        <span className="text-[11.5px] text-fg-subtle">{BREAKER_SHORT[s]}</span>
                        {sim ? (
                          <Badge tone="accent">شبیه‌ساز</Badge>
                        ) : (
                          <StatusBadge
                            status={
                              s === 'ok' ? 'completed' : s === 'down' ? 'rejected' : 'pending'
                            }
                            label={
                              s === 'ok' ? 'سالم' : s === 'down' ? 'قطع موقت' : 'در حال آزمایش'
                            }
                          />
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Remote>
          <p className="m-0 mt-2 text-[11.5px] text-fg-subtle">
            «شبیه‌ساز» یعنی محیط آزمایشی؛ وضعیت مدارشکن آن نشانه‌ی سلامت ارائه‌دهنده‌ی واقعی نیست.
          </p>
        </Card>
      </div>
      <Remote r={r}>
        {(o) => (
          <div className="flex flex-col gap-6" data-admin-overview>
            <p className="m-0 -mt-4 text-[12px] text-fg-subtle">
              به‌روزشده {formatRelative(o.asOf)} · فقط داده‌ی واقعی پایگاه داده
            </p>
            <ControlRoom ops={ops} />

            <Group title="کاربران">
              <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-5">
                <KpiTile label="کل کاربران" value={n(o.users.total)} />
                <KpiTile label="ثبت‌نام امروز" value={n(o.users.newToday)} />
                <KpiTile label="حساب‌های فعال" value={n(o.users.active)} />
                <KpiTile label="فعال در ۷ روز" value={n(o.users.activeLast7Days)} />
                <KpiTile
                  label="تعلیق / قفل"
                  value={n(o.users.suspended + o.users.locked)}
                  tone={o.users.suspended + o.users.locked > 0 ? 'negative' : 'neutral'}
                  hint={`تعلیق ${o.users.suspended.toLocaleString('fa-IR')} · قفل ${o.users.locked.toLocaleString('fa-IR')}`}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <KpiTile label="سطح ۰ (بدون احراز)" value={n(o.users.kyc0)} />
                <KpiTile label="سطح ۱" value={n(o.users.kyc1)} />
                <KpiTile label="سطح ۲" value={n(o.users.kyc2)} />
              </div>
            </Group>

            <Group
              title="صف‌ها"
              action={
                <Link
                  to="/kyc"
                  className="text-[12.5px] font-semibold text-fg-muted hover:text-fg hover:underline"
                >
                  صف احراز هویت
                </Link>
              }
            >
              <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6">
                <KpiTile
                  label="احراز در انتظار"
                  value={n(o.queues.kycPending)}
                  tone={o.queues.kycPending ? 'caution' : 'neutral'}
                />
                <KpiTile label="نیازمند اصلاح" value={n(o.queues.kycNeedsCorrection)} />
                <KpiTile
                  label="خطای سرویس استعلام"
                  value={n(o.queues.kycProviderUnavailable)}
                  tone={o.queues.kycProviderUnavailable ? 'negative' : 'neutral'}
                />
                <KpiTile label="تصمیم دستی امروز" value={n(o.queues.kycManualDecisionsToday)} />
                <KpiTile label="تکمیل‌شده امروز" value={n(o.queues.kycCompletedToday)} />
                <OpsKpi
                  to="/support"
                  label="پشتیبانی: منتظر کارشناس"
                  ops={ops}
                  pick={(s) => s.support.waitingAgent}
                  caution
                />
              </div>
            </Group>

            <Group
              title="مالی"
              action={
                o.financial.source === 'LEDGER' ? (
                  <Link
                    to="/reconciliation"
                    className="text-[12.5px] font-semibold text-fg-muted hover:text-fg hover:underline"
                  >
                    تطبیق و توقف اضطراری
                  </Link>
                ) : undefined
              }
            >
              {o.financial.source === 'NONE' && (
                <p className="m-0 text-[12.5px] text-fg-muted" data-financial-not-active>
                  {NOT_ACTIVE}؛ این شاخص‌ها پس از فعال‌شدن دفتر کل از پایگاه داده‌ی مالی نمایش داده
                  می‌شوند. «—» یعنی داده‌ای وجود ندارد، نه صفر.
                </p>
              )}
              {o.financial.source === 'LEDGER' && <MoneyStatus />}
              <div
                className="grid gap-3 sm:grid-cols-3 xl:grid-cols-6"
                data-financial-metrics={o.financial.source}
              >
                <KpiTile label="واریز امروز" value={toman(o.financial.depositsToday)} />
                <KpiTile label="برداشت امروز" value={toman(o.financial.withdrawalsToday)} />
                <KpiTile label="مبلغ در انتظار" value={toman(o.financial.pendingAmount)} />
                <OpsKpi
                  to="/conversions"
                  label="سفارش تبدیل امروز"
                  ops={ops}
                  pick={(s) => s.conversions.today}
                />
                <KpiTile label="کارمزد" value={toman(o.financial.fees)} />
                <KpiTile
                  label="تراکنش ناموفق"
                  value={
                    o.financial.failedTransactions === null
                      ? null
                      : n(o.financial.failedTransactions)
                  }
                />
              </div>
            </Group>

            <div className="grid gap-4 xl:grid-cols-2">
              <Card className="p-4 sm:p-5">
                <h2 className="m-0 mb-3 text-[14px] font-bold">رویدادهای امنیتی پرخطر اخیر</h2>
                {o.riskEvents.length === 0 ? (
                  <p className="m-0 text-[13px] text-fg-muted">رویداد پرخطری ثبت نشده است.</p>
                ) : (
                  <ul className="m-0 list-none divide-y divide-line p-0 text-[13px]">
                    {o.riskEvents.map((e) => (
                      <li key={e.id} className="flex items-center justify-between gap-2 py-2">
                        <Link
                          to="/users/$id"
                          params={{ id: e.userId }}
                          className="min-w-0 hover:underline"
                        >
                          {eventLabel(e.type)}
                          <span className="block truncate text-[12px] text-fg-muted">
                            {e.userName ?? 'کاربر'}
                          </span>
                        </Link>
                        <span className="flex shrink-0 flex-col items-end gap-1">
                          <Badge tone={SEVERITY[e.severity]?.tone ?? 'accent'}>
                            {SEVERITY[e.severity]?.label ?? e.severity}
                          </Badge>
                          <span className="text-[11.5px] text-fg-subtle">
                            {formatRelative(e.at)}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
              <Card className="p-4 sm:p-5">
                <h2 className="m-0 mb-3 text-[14px] font-bold">آخرین تصمیم‌های مدیریتی</h2>
                {o.recentDecisions.length === 0 ? (
                  <p className="m-0 text-[13px] text-fg-muted">تصمیمی ثبت نشده است.</p>
                ) : (
                  <ul className="m-0 list-none divide-y divide-line p-0 text-[13px]">
                    {o.recentDecisions.map((d) => (
                      <li key={d.id} className="py-2">
                        <Link to="/users/$id" params={{ id: d.userId }} className="hover:underline">
                          {auditLabel(d.action)}
                        </Link>
                        <span className="block text-[12px] text-fg-muted">
                          {d.userName ?? 'کاربر'} · <bdi dir="ltr">{d.admin}</bdi> ·{' '}
                          {formatRelative(d.at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          </div>
        )}
      </Remote>
    </AppContainer>
  );
}
